import { ConfigApi } from '@backstage/core-plugin-api';

/** How a status source's payload is interpreted. */
export type StatusSourceType = 'statuspage' | 'azure-devops' | 'rss';

export interface StatusSourceConfig {
  id: string;
  title: string;
  type: StatusSourceType;
  /** Path appended to the backend proxy base URL (`/api/proxy`). */
  proxyPath: string;
  /**
   * Optional narrowing: a Statuspage component name (e.g. `Copilot`) or an
   * Azure DevOps geography id (e.g. `US`, `CA`).
   */
  component?: string;
  /** Public status page opened when the chip is clicked. */
  link: string;
}

export interface IncidentConfig {
  outageCategory: string;
  maintenanceCategory: string;
  /** Hours after an announcement's start date during which its banner is shown. */
  windowHours: number;
  /** Hours before a scheduled maintenance starts during which it is announced. */
  upcomingHours: number;
}

export interface StatusConfig {
  enabled: boolean;
  refreshMinutes: number;
  sources: StatusSourceConfig[];
  incidents: IncidentConfig;
}

export interface HomeTip {
  title: string;
  body: string;
  link?: string;
  linkTitle?: string;
}

export const DEFAULT_STATUS_SOURCES: StatusSourceConfig[] = [
  {
    id: 'github',
    title: 'GitHub',
    type: 'statuspage',
    proxyPath: '/devhub-status/github/summary.json',
    link: 'https://www.githubstatus.com',
  },
  {
    id: 'copilot',
    title: 'GitHub Copilot',
    type: 'statuspage',
    proxyPath: '/devhub-status/github/summary.json',
    component: 'Copilot',
    link: 'https://www.githubstatus.com',
  },
  {
    id: 'azure-devops',
    title: 'Azure DevOps',
    type: 'azure-devops',
    proxyPath: '/devhub-status/azure-devops/health?api-version=7.1-preview.1',
    link: 'https://status.dev.azure.com',
  },
  {
    id: 'jfrog',
    title: 'JFrog',
    type: 'statuspage',
    proxyPath: '/devhub-status/jfrog/summary.json',
    link: 'https://status.jfrog.io',
  },
  {
    id: 'azure',
    title: 'Azure',
    type: 'rss',
    proxyPath: '/devhub-status/azure/feed/',
    link: 'https://azure.status.microsoft/en-us/status',
  },
];

const STATUS_TYPES: StatusSourceType[] = ['statuspage', 'azure-devops', 'rss'];

export function readStatusConfig(configApi: ConfigApi): StatusConfig {
  const status = configApi.getOptionalConfig('devhub.home.status');
  const configuredSources = status?.getOptionalConfigArray('sources');
  const sources = configuredSources
    ? configuredSources
        .map(source => ({
          id: source.getString('id'),
          title: source.getString('title'),
          type: source.getString('type') as StatusSourceType,
          proxyPath: source.getString('proxyPath'),
          component: source.getOptionalString('component'),
          link: source.getString('link'),
        }))
        .filter(source => STATUS_TYPES.includes(source.type))
    : DEFAULT_STATUS_SOURCES;

  const incidents = status?.getOptionalConfig('incidents');
  return {
    enabled: status?.getOptionalBoolean('enabled') ?? true,
    refreshMinutes: Math.max(1, status?.getOptionalNumber('refreshMinutes') ?? 5),
    sources,
    incidents: {
      outageCategory: incidents?.getOptionalString('outageCategory') ?? 'outage',
      maintenanceCategory: incidents?.getOptionalString('maintenanceCategory') ?? 'maintenance',
      windowHours: incidents?.getOptionalNumber('windowHours') ?? 72,
      upcomingHours: incidents?.getOptionalNumber('upcomingHours') ?? 24,
    },
  };
}

export function readTips(configApi: ConfigApi): HomeTip[] {
  return (configApi.getOptionalConfigArray('devhub.home.tips') ?? []).map(tip => ({
    title: tip.getString('title'),
    body: tip.getString('body'),
    link: tip.getOptionalString('link'),
    linkTitle: tip.getOptionalString('linkTitle'),
  }));
}
